import React from "react";
import { BannerProps } from "@/types/banner";
import { defaultBannerContent } from "./defaultBannerContent";
import { useBannerRotation } from "./useBannerRotation";
import { LinkWrapper } from "@/components/LinkWrapper";
import { cn } from "@/lib/utils";

const TraditionalBanner: React.FC<BannerProps> = ({
  items = defaultBannerContent,
  rotationInterval = 5000,
  ...forwardedProps
}) => {
  const { currentIndex, isTransitioning, pause, resume } = useBannerRotation({
    itemCount: items.length,
    interval: rotationInterval,
  });

  if (items.length === 0) {
    return null;
  }

  const currentItem = items[currentIndex];
  const hasMultipleItems = items.length > 1;

  return (
    <div
      role="region"
      aria-label="Announcements"
      className="relative w-full bg-gradient-to-r from-primary/30 to-primary/20 py-3 sm:py-2.5"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      {...forwardedProps}
    >
      <div className="container mx-auto px-4">
        {/* Fixed height container to prevent layout shift during rotation */}
        <div className="relative flex h-[52px] items-center justify-center overflow-hidden sm:h-[28px]">
          <div
            aria-live="polite"
            aria-atomic="true"
            className={cn(
              "flex flex-col items-center justify-center gap-1.5 text-center transition-opacity duration-300 ease-in-out sm:flex-row sm:gap-2",
              isTransitioning ? "opacity-0" : "opacity-100"
            )}
          >
            <span className="text-xs font-medium leading-tight text-foreground sm:text-sm">
              {currentItem.text}
              {/* Screen reader context for rotating content */}
              {hasMultipleItems && (
                <span className="sr-only">
                  {` (Announcement ${currentIndex + 1} of ${items.length})`}
                </span>
              )}
            </span>
            {currentItem.linkText && currentItem.linkUrl && (
              <LinkWrapper
                to={currentItem.linkUrl}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 sm:px-3 sm:text-sm"
              >
                {currentItem.linkText}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 sm:h-3.5 sm:w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </LinkWrapper>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraditionalBanner;
